/**
 * Validates if quilt dimensions are valid and error-free
 */
export const isQuiltDimensionsValid = (gridSize, quiltDimensions) => {
    // Enable debug mode for verbose logging
    const DEBUG = true;
    
    // Check if gridSize is valid
    if (!gridSize || !gridSize.includes('x')) {
        DEBUG && console.log("Invalid dimensions: gridSize format is invalid");
        return false;
    }

    // Get the dimension values directly from form fields as a double-check
    // This helps catch cases where the form values don't match the state values
    const quiltWidthInput = document.querySelector('input[name="quiltWidth"]')?.value;
    const quiltHeightInput = document.querySelector('input[name="quiltHeight"]')?.value;
    const tileWidthInput = document.querySelector('input[name="tileWidth"]')?.value;
    const tileHeightInput = document.querySelector('input[name="tileHeight"]')?.value;
    
    // Check input fields first - if any are empty or invalid, validation fails
    if (!quiltWidthInput || !quiltHeightInput || !tileWidthInput || !tileHeightInput) {
        DEBUG && console.log("Invalid dimensions: missing form input values", {
            quiltWidthInput, quiltHeightInput, tileWidthInput, tileHeightInput
        });
        return false;
    }
    
    // Parse input values to numbers for additional validation
    const quiltWidthValue = parseFloat(quiltWidthInput);
    const quiltHeightValue = parseFloat(quiltHeightInput);
    const tileWidthValue = parseFloat(tileWidthInput);
    const tileHeightValue = parseFloat(tileHeightInput);
    
    // Check for NaN values
    if (isNaN(quiltWidthValue) || isNaN(quiltHeightValue) || 
        isNaN(tileWidthValue) || isNaN(tileHeightValue)) {
        DEBUG && console.log("Invalid dimensions: non-numeric input values");
        return false;
    }
    
    // Define size limits
    const quiltSizeLimits = { min: 4, max: 8 }; // feet
    const tileSizeLimits = { min: 6, max: 15 }; // inches
    
    // Check dimension values directly from inputs
    if (tileHeightValue < tileSizeLimits.min) {
        DEBUG && console.log("Invalid dimensions: tile height too small from input", tileHeightValue);
        return false;
    }
    
    if (tileWidthValue < tileSizeLimits.min) {
        DEBUG && console.log("Invalid dimensions: tile width too small from input", tileWidthValue);
        return false;
    }
    
    // Now also check state values
    if (!quiltDimensions.quiltWidth || 
        !quiltDimensions.quiltHeight || 
        !quiltDimensions.tileWidth || 
        !quiltDimensions.tileHeight) {
        DEBUG && console.log("Invalid dimensions: missing values in state");
        return false;
    }
    
    // Critical check: ensure tile height meets minimum requirement (common failure point)
    if (quiltDimensions.tileHeight < tileSizeLimits.min) {
        DEBUG && console.log("Invalid dimensions: tile height too small in state", quiltDimensions.tileHeight);
        return false;
    }
    
    if (quiltDimensions.tileWidth < tileSizeLimits.min) {
        DEBUG && console.log("Invalid dimensions: tile width too small in state", quiltDimensions.tileWidth);
        return false;
    }
    
    // Check size limits
    if (quiltDimensions.quiltWidth < quiltSizeLimits.min || 
        quiltDimensions.quiltWidth > quiltSizeLimits.max ||
        quiltDimensions.quiltHeight < quiltSizeLimits.min || 
        quiltDimensions.quiltHeight > quiltSizeLimits.max) {
        DEBUG && console.log("Invalid dimensions: quilt size out of range");
        return false;
    }
    
    if (quiltDimensions.tileWidth > tileSizeLimits.max) {
        DEBUG && console.log("Invalid dimensions: tile width too large", quiltDimensions.tileWidth);
        return false;
    }
    
    if (quiltDimensions.tileHeight > tileSizeLimits.max) {
        DEBUG && console.log("Invalid dimensions: tile height too large", quiltDimensions.tileHeight);
        return false;
    }
    
    // Check for error messages in the DOM
    const errorElements = document.querySelectorAll('.text-red-500');
    const hasValidationErrors = Array.from(errorElements).some(el => 
        el.textContent && 
        (el.textContent.includes('Min') || 
        el.textContent.includes('Max') ||
        el.textContent.includes('required'))
    );
    
    if (hasValidationErrors) {
        DEBUG && console.log("Invalid dimensions: validation errors found in UI");
        return false;
    }
    
    // Check quilt width-height constraint
    if (quiltDimensions.quiltWidth > quiltDimensions.quiltHeight) {
        DEBUG && console.log("Invalid dimensions: quilt width > height constraint violated");
        return false;
    }
    
    // Check tile width-height constraint
    if (quiltDimensions.tileWidth > quiltDimensions.tileHeight) {
        DEBUG && console.log("Invalid dimensions: tile width > height constraint violated");
        return false;
    }
    
    // Additional validation for reasonable grid size
    const columnsFromDimensions = Math.floor((quiltDimensions.quiltWidth * 12) / quiltDimensions.tileWidth);
    const rowsFromDimensions = Math.floor((quiltDimensions.quiltHeight * 12) / quiltDimensions.tileHeight);
    
    if (columnsFromDimensions < 4 || rowsFromDimensions < 4) {
        DEBUG && console.log("Invalid dimensions: resulting grid too small from calculation", 
            {cols: columnsFromDimensions, rows: rowsFromDimensions});
        return false;
    }
    
    // All checks passed
    DEBUG && console.log("All dimension validation checks passed");
    return true;
};

/**
 * Handles dimension changes from QuiltSetup component
 */
export const handleDimensionsChange = (
    dimensions,
    generated,
    uploadedImages,
    setQuiltDimensions,
    setRequiredImages,
    setGridSize,
    gridSize,
    setShowWarning
) => {
    console.log("handleDimensionsChange called with:", dimensions);
    
    // Skip if no dimensions (happens when resetting)
    if (!dimensions || !dimensions.quiltWidth || !dimensions.quiltHeight) {
        return;
    }
    
    // Calculate missing properties if needed
    if (!dimensions.tileWidthPixels) {
        dimensions.tileWidthPixels = 300;
    }
    if (!dimensions.tileHeightPixels) {
        dimensions.tileHeightPixels = 300;
    }
    
    // Update the dimensions state
    setQuiltDimensions(dimensions);
    
    // Check if we need to update grid size
    if (dimensions.columns > 0 && dimensions.rows > 0) {
        const newGridSize = `${dimensions.columns}x${dimensions.rows}`;
        
        // Only update grid size if it's different from current one
        if (newGridSize !== gridSize) {
            // Don't reset required images for the same grid size
            setGridSize(newGridSize);
            
            // Preserve uploaded images when changing dimensions
            // Just update the required images count without showing warnings
            const requiredImages = dimensions.columns * dimensions.rows;
            setRequiredImages(requiredImages);
            
            // Only show warning about required images if we already have images uploaded
            if (uploadedImages && uploadedImages.length > 0) {
                if (uploadedImages.length < requiredImages) {
                    setShowWarning(`Please upload ${requiredImages - uploadedImages.length} more T-shirts for the ${newGridSize} grid.`);
                } else if (uploadedImages.length > requiredImages) {
                    setShowWarning(`You have uploaded more than required. You can only use ${requiredImages} T-shirts for the ${newGridSize} grid.`);
                } else {
                    setShowWarning(''); // Exact number of images
                }
            } else {
                setShowWarning(''); // No images uploaded yet
            }
        }
    }
};
